import { create } from 'zustand';

const PLATFORM_URL =
  (import.meta.env['VITE_PLATFORM_URL'] as string | undefined) ||
  'http://localhost:7001';

const ACCESS_TOKEN_KEY = 'mytile_access_token';
const REFRESH_TOKEN_KEY = 'mytile_refresh_token';

export type AuthStatus = 'unknown' | 'loading' | 'authenticated' | 'unauthenticated';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  accountId: number | null;
  displayName: string | null;
  discriminator: number | null;
  login(accessToken: string, refreshToken: string): void;
  logout(): void;
  refresh(): Promise<boolean>;
  initialize(): Promise<void>;
}

let refreshPromise: Promise<boolean> | null = null;

/** @internal — test-only: reset the in-flight refresh mutex */
export function _resetRefreshPromise(): void {
  refreshPromise = null;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function extractIdentity(token: string): {
  accountId: number | null;
  displayName: string | null;
  discriminator: number | null;
} {
  const payload = decodeJwtPayload(token);
  if (!payload) return { accountId: null, displayName: null, discriminator: null };
  return {
    accountId: typeof payload['sub'] === 'string' ? Number(payload['sub']) : null,
    displayName: typeof payload['name'] === 'string' ? (payload['name'] as string) : null,
    discriminator: typeof payload['disc'] === 'number' ? (payload['disc'] as number) : null,
  };
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'unknown',
  accessToken: null,
  accountId: null,
  displayName: null,
  discriminator: null,

  login(accessToken: string, refreshToken: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    const identity = extractIdentity(accessToken);
    set({
      status: 'authenticated',
      accessToken,
      ...identity,
    });
  },

  logout() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    set({
      status: 'unauthenticated',
      accessToken: null,
      accountId: null,
      displayName: null,
      discriminator: null,
    });
  },

  async refresh(): Promise<boolean> {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) return false;

      try {
        const res = await fetch(`${PLATFORM_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!res.ok) {
          get().logout();
          return false;
        }

        const data = (await res.json()) as TokenResponse;
        get().login(data.access_token, data.refresh_token);
        return true;
      } catch {
        get().logout();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  },

  async initialize() {
    const stored = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!stored) {
      set({ status: 'unauthenticated' });
      return;
    }
    set({ status: 'loading' });
    await get().refresh();
  },
}));
